import React, { useState, useEffect } from 'react';
import { TransactionData } from './AddTransactionFlow';
import { Icon } from '../Icon';
import { useAppStore } from '../../store/useAppStore';
import { formatCurrency } from '../../utils/format';
import { apiClient } from '../../api/client';
import { PaymentMethod } from '../../types';
import { ReceiptItemsEditor, ReceiptItem } from '../ReceiptItemsEditor';
import './TransactionConfirmation.css';

interface TransactionConfirmationProps {
  data: TransactionData;
  onConfirm: () => void;
  onEdit: (step: any) => void;
  onBack: () => void;
  isSubmitting: boolean;
  currency?: string;
  onSkip?: () => void; // Для пропуска чека в bulk upload
  showSkip?: boolean; // Показывать ли кнопку пропуска
  onUpdateData?: (updates: Partial<TransactionData>) => void; // Для inline обновления
}

const frequencyLabels: Record<string, string> = {
  onetime: 'Разовый',
  daily: 'Ежедневный',
  weekly: 'Еженедельный',
  monthly: 'Ежемесячный',
  yearly: 'Годовой'
};

const weekDays = ['Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота', 'Воскресенье'];

export const TransactionConfirmation: React.FC<TransactionConfirmationProps> = ({
  data,
  onConfirm,
  onEdit,
  onBack,
  isSubmitting,
  currency = 'RUB',
  onSkip,
  showSkip = false,
  onUpdateData
}) => {
  const { goals } = useAppStore();
  const [description, setDescription] = useState(data.description || '');
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  
  // Payment Methods
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [showPaymentMethodModal, setShowPaymentMethodModal] = useState(false);
  const [loadingMethods, setLoadingMethods] = useState(false);

  useEffect(() => {
    loadPaymentMethods();
  }, []);

  const loadPaymentMethods = async () => {
    try {
      setLoadingMethods(true);
      const response = await apiClient.getPaymentMethods();
      setPaymentMethods(response.payment_methods || []);
    } catch (error) {
      console.error('Error loading payment methods:', error);
    } finally {
      setLoadingMethods(false);
    }
  };

  const getPaymentMethodName = (methodId: string | undefined) => {
    if (!methodId) return 'Основной счет';
    const method = paymentMethods.find(m => m.id.toString() === methodId.toString());
    return method ? method.name : 'Основной счет';
  };

  const handlePaymentMethodSelect = (methodId: string | undefined) => {
    if (onUpdateData) {
      onUpdateData({ payment_method_id: methodId });
    }
    setShowPaymentMethodModal(false);
  };
  const getDateDisplay = () => {
    if (data.frequency === 'onetime' && data.date) {
      const dateStr = data.date.toLocaleDateString('ru-RU');
      const timeStr = data.date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
      return `${dateStr}, ${timeStr}`;
    }
    if (data.frequency === 'daily' && data.date) {
      const dateStr = data.date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
      return `Начиная с ${dateStr}`;
    }
    if (data.frequency === 'weekly' && data.date) {
      const dateStr = data.date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
      const dayOfWeek = data.dayOfWeek !== undefined ? weekDays[data.dayOfWeek] : '';
      return `${dayOfWeek} (с ${dateStr})`;
    }
    if (data.frequency === 'monthly' && data.date) {
      const dateStr = data.date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
      const dayOfMonth = data.dayOfMonth || data.date.getDate();
      return `${dayOfMonth} число (с ${dateStr})`;
    }
    if (data.frequency === 'yearly') {
      const monthNames = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];
      if (data.dayAndMonth) {
        return `${data.dayAndMonth.day} ${monthNames[data.dayAndMonth.month - 1]}`;
      }
      if (data.date) {
        const dateStr = data.date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
        return dateStr;
      }
    }
    return 'Не указано';
  };

  return (
    <div className="transaction-confirmation step-content">
      <div className="transaction-confirmation__header">
        <button className="transaction-confirmation__back neo-raised" onClick={onBack}>
          <Icon name="chevron_left" size={20} />
        </button>
        <div className="transaction-confirmation__header-content">
          <h2 className="transaction-confirmation__title">Подтверждение</h2>
          <p className="transaction-confirmation__subtitle">Проверьте данные</p>
        </div>
      </div>

      <div className="transaction-confirmation__content">
        <div className="confirmation-card">
          <div className="confirmation-card__header">
            <div className="confirmation-card__icon" style={{
              border: `2px solid ${data.type === 'expense' ? '#FF3B30' : '#34C759'}`,
              background: 'transparent'
            }}>
              <Icon name={data.type === 'expense' ? 'trending-down' : 'trending-up'} size={36} color={data.type === 'expense' ? '#FF3B30' : '#34C759'} />
            </div>
            <div className="confirmation-card__amount">
              {(data.amount || 0).toLocaleString('ru-RU')} {currency}
            </div>
          </div>

          <div className="confirmation-details">
            {/* Тип - не редактируется */}
            <div className="confirmation-detail">
              <div className="confirmation-detail__content">
                <div className="confirmation-detail__label">Тип</div>
                <div className="confirmation-detail__value">
                  {data.type === 'expense' ? 'Расход' : 'Доход'}
                </div>
              </div>
            </div>

            <div className="confirmation-detail confirmation-detail--editable" onClick={() => onEdit('frequency')}>
              <div className="confirmation-detail__content">
                <div className="confirmation-detail__label">Частота</div>
                <div className="confirmation-detail__value">
                  {frequencyLabels[data.frequency]}
                </div>
              </div>
              <div className="confirmation-detail__edit"><Icon name="edit" size={14} /></div>
            </div>

            <div className="confirmation-detail confirmation-detail--editable" onClick={() => onEdit('title')}>
              <div className="confirmation-detail__content">
                <div className="confirmation-detail__label">Название</div>
                <div className="confirmation-detail__value">{data.title}</div>
              </div>
              <div className="confirmation-detail__edit"><Icon name="edit" size={14} /></div>
            </div>

            <div 
              className={`confirmation-detail ${!data.receipt_items || data.receipt_items.length === 0 ? 'confirmation-detail--editable' : ''}`} 
              onClick={!data.receipt_items || data.receipt_items.length === 0 ? () => onEdit('amount') : undefined}
            >
              <div className="confirmation-detail__content">
                <div className="confirmation-detail__label">
                  Сумма
                  {data.receipt_items && data.receipt_items.length > 0 && (
                    <span style={{ fontSize: '11px', color: '#8E8E93', marginLeft: '6px' }}>(из списка)</span>
                  )}
                </div>
                <div className="confirmation-detail__value">
                  {(data.amount || 0).toLocaleString('ru-RU')} {currency}
                </div>
              </div>
              {(!data.receipt_items || data.receipt_items.length === 0) && (
                <div className="confirmation-detail__edit"><Icon name="edit" size={14} /></div>
              )}
            </div>

            {/* Показываем дату для всех типов транзакций */}
            {(data.date || data.dayAndMonth) && (
              <div className="confirmation-detail confirmation-detail--editable" onClick={() => onEdit('date')}>
                <div className="confirmation-detail__content">
                  <div className="confirmation-detail__label">
                    {data.frequency === 'onetime' 
                      ? 'Дата и время' 
                      : (data.frequency === 'daily' || data.frequency === 'weekly' || data.frequency === 'monthly')
                        ? 'Дата начала'
                        : 'Дата'}
                  </div>
                  <div className="confirmation-detail__value">{getDateDisplay()}</div>
                </div>
                <div className="confirmation-detail__edit">✎</div>
              </div>
            )}

            {data.category && (
              <div className="confirmation-detail confirmation-detail--editable" onClick={() => onEdit('category')}>
                <div className="confirmation-detail__content">
                  <div className="confirmation-detail__label">Категория</div>
                  <div className="confirmation-detail__value">{data.category}</div>
                </div>
                <div className="confirmation-detail__edit">✎</div>
              </div>
            )}

            {/* Payment Method / Счет */}
            {!data.source_goal_id && (
              <div className="confirmation-detail confirmation-detail--editable" onClick={() => setShowPaymentMethodModal(true)}>
                <div className="confirmation-detail__content">
                  <div className="confirmation-detail__label">Счет</div>
                  <div className="confirmation-detail__value">
                    <Icon name="credit_card" size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                    {getPaymentMethodName(data.payment_method_id)}
                  </div>
                </div>
                <div className="confirmation-detail__edit">✎</div>
              </div>
            )}

            {data.type === 'expense' && data.source_type === 'goal' && data.source_goal_id && (() => {
              const goal = goals.find(g => g.id === data.source_goal_id);
              return goal ? (
                <div className="confirmation-detail">
                  <div className="confirmation-detail__label">Списать с цели</div>
                  <div className="confirmation-detail__value">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {goal.icon ? (
                        <span>{goal.icon}</span>
                      ) : (
                        <Icon name="target" size={20} color="var(--color-primary)" />
                      )}
                      <span>{goal.name}</span>
                    </div>
                    <div style={{ fontSize: '12px', color: '#8E8E93', marginTop: '2px' }}>
                      Доступно: {formatCurrency(goal.current_amount, currency)}
                    </div>
                  </div>
                </div>
              ) : null;
            })()}

            {/* Напоминания */}
            {data.frequency !== 'onetime' && (
              <div className="confirmation-detail confirmation-detail--editable" onClick={() => onEdit('reminders')}>
                <div className="confirmation-detail__content">
                  <div className="confirmation-detail__label">Напоминания</div>
                  <div className="confirmation-detail__value">
                    {data.remindersEnabled && data.reminderTimes && data.reminderTimes.length > 0 
                      ? (() => {
                          const offsetLabels = {
                            0: 'В день',
                            1: 'За 1 день',
                            3: 'За 3 дня',
                            7: 'За 7 дней',
                            10: 'За 10 дней'
                          };
                          const offsets = data.reminderOffsets || [0];
                          const offsetsText = offsets.map(o => offsetLabels[o as keyof typeof offsetLabels] || `За ${o} дней`).join(', ');
                          const timesText = data.reminderTimes.join(', ');
                          const totalCount = offsets.length * data.reminderTimes.length;
                          return `${totalCount} ${totalCount === 1 ? 'напоминание' : totalCount > 4 ? 'напоминаний' : 'напоминания'} (${offsetsText} • ${timesText})`;
                        })()
                      : 'Выключены'}
                  </div>
                </div>
                <div className="confirmation-detail__edit">✎</div>
              </div>
            )}

            {/* Время уведомления */}
            {data.frequency !== 'onetime' && data.notificationTime && (
              <div className="confirmation-detail confirmation-detail--editable" onClick={() => onEdit('notification')}>
                <div className="confirmation-detail__content">
                  <div className="confirmation-detail__label">Уведомление</div>
                  <div className="confirmation-detail__value">{data.notificationTime}</div>
                </div>
                <div className="confirmation-detail__edit">✎</div>
              </div>
            )}

            {/* Описание (inline редактирование) */}
            <div className="confirmation-detail" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
              <div className="confirmation-detail__label" style={{ marginBottom: '8px' }}>Описание</div>
              {isEditingDescription ? (
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  onBlur={() => {
                    setIsEditingDescription(false);
                    if (onUpdateData) {
                      onUpdateData({ description });
                    }
                  }}
                  autoFocus
                  placeholder="Добавьте описание..."
                  style={{
                    width: '100%',
                    minHeight: '80px',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid #E5E5EA',
                    fontSize: '14px',
                    resize: 'vertical',
                    fontFamily: 'inherit'
                  }}
                />
              ) : (
                <div
                  onClick={() => setIsEditingDescription(true)}
                  style={{
                    padding: '12px',
                    borderRadius: '8px',
                    background: 'transparent',
                    border: '2px solid var(--color-divider)',
                    minHeight: '60px',
                    fontSize: '14px',
                    color: description ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
                    cursor: 'pointer',
                    whiteSpace: 'pre-wrap'
                  }}
                >
                  {description || 'Нажмите, чтобы добавить описание...'}
                </div>
              )}
            </div>

            {/* Редактируемый список покупок из чека */}
            {data.receipt_items && data.receipt_items.length > 0 && (
              <ReceiptItemsEditor
                items={data.receipt_items as ReceiptItem[]}
                currency={data.currency || currency}
                onItemsChange={(newItems) => {
                  if (onUpdateData) {
                    onUpdateData({ receipt_items: newItems });
                  }
                }}
                onTotalAmountChange={(total) => {
                  // Обновляем общую сумму транзакции
                  if (onUpdateData) {
                    onUpdateData({ amount: total });
                  }
                }}
              />
            )}
          </div>
        </div>
      </div>

      <div className="transaction-confirmation__footer" style={{ display: 'flex', gap: '12px', flexDirection: showSkip ? 'column' : 'row' }}>
        {showSkip && onSkip && (
          <button
            className="transaction-confirmation__skip"
            onClick={onSkip}
            disabled={isSubmitting}
            style={{
              width: '100%',
              padding: '14px',
              background: 'transparent',
              border: '2px solid var(--color-divider)',
              borderRadius: '14px',
              color: 'var(--color-text-secondary)',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            Пропустить чек
          </button>
        )}
        
        <button
          className="transaction-confirmation__submit neo-raised"
          onClick={onConfirm}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Сохранение...' : 'Подтвердить'}
        </button>
      </div>

      {/* Модальное окно для выбора счета */}
      {showPaymentMethodModal && (
        <div className="payment-method-modal">
          <div className="payment-method-modal__overlay" onClick={() => setShowPaymentMethodModal(false)} />
          <div className="payment-method-modal__content">
            <div className="payment-method-modal__header">
              <h3>Выберите счет</h3>
              <button onClick={() => setShowPaymentMethodModal(false)}>
                <Icon name="close" size={20} />
              </button>
            </div>
            
            <div className="payment-method-modal__list">
              {/* Основной счет */}
              <div 
                className={`payment-method-item ${!data.payment_method_id ? 'payment-method-item--selected' : ''}`}
                onClick={() => handlePaymentMethodSelect(undefined)}
              >
                <div className="payment-method-item__icon">
                  <Icon name="wallet" size={20} />
                </div>
                <div className="payment-method-item__info">
                  <div className="payment-method-item__name">Основной счет</div>
                  <div className="payment-method-item__desc">По умолчанию</div>
                </div>
                {!data.payment_method_id && (
                  <Icon name="check" size={20} color="#667eea" />
                )}
              </div>

              {/* Счета пользователя */}
              {paymentMethods.map(method => (
                <div 
                  key={method.id}
                  className={`payment-method-item ${data.payment_method_id === method.id.toString() ? 'payment-method-item--selected' : ''}`}
                  onClick={() => handlePaymentMethodSelect(method.id.toString())}
                >
                  <div className="payment-method-item__icon">
                    <Icon name="credit_card" size={20} />
                  </div>
                  <div className="payment-method-item__info">
                    <div className="payment-method-item__name">{method.name}</div>
                  </div>
                  {data.payment_method_id === method.id.toString() && (
                    <Icon name="check" size={20} color="#667eea" />
                  )}
                </div>
              ))}

              {paymentMethods.length === 0 && !loadingMethods && (
                <div style={{ padding: '20px', textAlign: 'center', color: '#8E8E93', fontSize: '14px' }}>
                  У вас пока нет дополнительных счетов.
                  <br />
                  Добавьте их в настройках.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
